import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Search, FileSearch, Send, CheckCircle2, Clock, MessageSquare, ExternalLink, AlertCircle, Sparkles, Loader2, FileText } from "lucide-react";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { useSubmitting } from "@/lib/useSubmitting";

const STATUS_META = {
  approved_to_seo: { label: "Awaiting Post", color: "bg-amber-100 text-amber-700 border-amber-200", Icon: Clock },
  posted: { label: "Posted ✓", color: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
};

export default function SEODashboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | approved_to_seo | posted
  const [open, setOpen] = useState(null); // workflow row currently being viewed
  const [blog, setBlog] = useState(null);
  const [comment, setComment] = useState("");
  const [postedUrl, setPostedUrl] = useState("");
  const commentSubmit = useSubmitting();
  const postSubmit = useSubmitting();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/blog-workflow");
      setRows(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openBlog = async (row) => {
    setOpen(row); setBlog(null); setComment(""); setPostedUrl(row.posted_url || "");
    try {
      const r = await api.get(`/blog-workflow/${row.doc_id}/blog`);
      setBlog(r.data);
    } catch (e) {
      toast.error("Couldn't load blog content");
    }
  };

  const addComment = () =>
    commentSubmit.run(async () => {
      if (!comment.trim()) { toast.error("Comment is empty"); return; }
      try {
        const r = await api.post(`/blog-workflow/${open.doc_id}/comments`, { text: comment.trim() });
        setOpen((o) => ({ ...o, seo_comments: r.data.seo_comments }));
        setComment("");
        toast.success("Comment sent to Head of Media");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not add comment");
      }
    });

  const markPosted = () =>
    postSubmit.run(async () => {
      if (!window.confirm("Mark this blog as POSTED? After this you can no longer add comments.")) return;
      try {
        const r = await api.post(`/blog-workflow/${open.doc_id}/mark-posted`, { posted_url: postedUrl.trim() });
        setOpen((o) => ({ ...o, ...r.data }));
        toast.success("Blog marked as posted ✓");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not mark as posted");
      }
    });

  const filtered = useMemo(() => {
    let r = rows;
    if (filter !== "all") r = r.filter((x) => x.status === filter);
    if (search) {
      const s = search.toLowerCase();
      r = r.filter((x) => (x.title || "").toLowerCase().includes(s) || (x.author_name || "").toLowerCase().includes(s));
    }
    return r;
  }, [rows, search, filter]);

  const totals = useMemo(() => ({
    queue: rows.filter((r) => r.status === "approved_to_seo").length,
    posted: rows.filter((r) => r.status === "posted").length,
    needs_followup: rows.filter((r) => r.status === "approved_to_seo" && (r.seo_comments || []).length > 0).length,
  }), [rows]);

  return (
    <Layout navItems={getNav("seo")}>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto">
        <PageHeader
          title={`SEO Desk · ${user.name.split(" ")[0]}`}
          subtitle="Blogs approved by the Head of Media & Comm. Review, comment, and mark as posted."
          icon={Sparkles}
          accent="lime"
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="In Queue" value={totals.queue} icon={Clock} color="amber" trend="Awaiting post" />
          <StatCard label="Posted" value={totals.posted} icon={CheckCircle2} color="emerald" trend="Live blogs" />
          <StatCard label="Pending Replies" value={totals.needs_followup} icon={MessageSquare} color="pink" trend="From Head" />
        </div>

        <div className="dashboard-card p-5 mb-6">
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input className="input-pill w-full" style={{ paddingLeft: "2.5rem" }} placeholder="Search by title or author…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="seo-search" />
            </div>
            <div className="flex gap-2">
              {[
                { k: "all", label: "All" },
                { k: "approved_to_seo", label: "Awaiting Post" },
                { k: "posted", label: "Posted" },
              ].map((f) => (
                <button key={f.k} onClick={() => setFilter(f.k)} className={`px-4 py-2 rounded-full text-xs font-bold transition ${filter === f.k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`seo-filter-${f.k}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <FileSearch className="w-10 h-10 mx-auto mb-2" />
              No blogs in this view yet.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((row) => {
                const meta = STATUS_META[row.status] || { label: row.status, color: "bg-slate-100 text-slate-600 border-slate-200", Icon: AlertCircle };
                const StatusIcon = meta.Icon;
                return (
                  <button key={row.doc_id} onClick={() => openBlog(row)} className="w-full text-left py-3 px-2 hover:bg-slate-50 transition flex items-center gap-4 rounded-lg" data-testid={`seo-row-${row.doc_id}`}>
                    <div className={`px-2.5 py-1 rounded-full text-xs font-bold border ${meta.color} flex items-center gap-1 shrink-0`}>
                      <StatusIcon className="w-3 h-3" /> {meta.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{row.title}</div>
                      <div className="text-xs text-slate-500">By {row.author_name || "—"} · approved {row.approved_at ? new Date(row.approved_at).toLocaleDateString() : "—"}{row.posted_at ? ` · posted ${new Date(row.posted_at).toLocaleDateString()}` : ""}</div>
                    </div>
                    {(row.seo_comments || []).length > 0 && (
                      <span className="text-xs text-pink-600 flex items-center gap-1 shrink-0"><MessageSquare className="w-3 h-3" /> {row.seo_comments.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col relative">
              <ModalCloseBtn onClose={() => { setOpen(null); setBlog(null); }} />
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start gap-3 pr-10">
                  <div className="flex-1">
                    <div className="text-xs text-slate-500 uppercase tracking-wide font-bold mb-1">{open.author_name} · {open.author_role}</div>
                    <h3 className="font-display font-black text-2xl">{open.title || "Untitled blog"}</h3>
                  </div>
                  <div className={`px-3 py-1.5 rounded-full text-xs font-bold border ${(STATUS_META[open.status] || {}).color || "bg-slate-100"} shrink-0`}>
                    {(STATUS_META[open.status] || {}).label || open.status}
                  </div>
                </div>
                {open.posted_url && (
                  <a href={open.posted_url} target="_blank" rel="noreferrer" className="text-xs text-lime-700 hover:underline inline-flex items-center gap-1 mt-2"><ExternalLink className="w-3 h-3" /> {open.posted_url}</a>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-6 grid lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3 space-y-3">
                  {!blog && (
                    <div className="text-slate-400 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                  )}
                  {blog && (() => {
                    const d = blog.doc || {};
                    const gdocUrl = d.gdoc_web_url || (d.gdoc_id ? `https://docs.google.com/document/d/${d.gdoc_id}/edit` : null);
                    const previewUrl = d.gdoc_id ? `https://docs.google.com/document/d/${d.gdoc_id}/preview` : null;
                    const html = d.content_html || "";
                    const htmlEmpty = !html.replace(/<[^>]+>/g, "").trim();
                    if (gdocUrl) {
                      // Google-Docs-backed blog → iframe preview + Open button.
                      return (
                        <>
                          <div className="flex items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2">
                            <div className="text-xs text-sky-900 flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 shrink-0" />
                              <span className="truncate">Google Doc — live source</span>
                            </div>
                            <a href={gdocUrl} target="_blank" rel="noreferrer"
                               className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 shrink-0"
                               data-testid="seo-open-gdoc-btn">
                              <ExternalLink className="w-3 h-3" /> Open in Google Docs
                            </a>
                          </div>
                          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" style={{ minHeight: 500 }}>
                            <iframe src={previewUrl} title={d.title || "Blog preview"}
                                    className="w-full" style={{ height: 560, border: "none" }}
                                    data-testid="seo-gdoc-preview-iframe" />
                          </div>
                          <p className="text-[11px] text-slate-400 leading-relaxed">
                            Preview not loading? It means the doc isn&apos;t shared with your Google account yet — click <b>Open in Google Docs</b> above and request access from the author.
                          </p>
                        </>
                      );
                    }
                    // Legacy HTML-backed blog → render inline
                    return (
                      <div className="prose prose-sm max-w-none">
                        <div dangerouslySetInnerHTML={{ __html: htmlEmpty ? "<p><em>This blog has no content yet.</em></p>" : html }} />
                      </div>
                    );
                  })()}
                </div>
                <div className="lg:col-span-2 space-y-4">
                  <div className="border border-slate-200 rounded-xl p-4">
                    <div className="text-xs uppercase font-bold text-slate-500 mb-2">Comments to Head</div>
                    <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                      {(open.seo_comments || []).length === 0 && <div className="text-xs text-slate-400">No comments yet.</div>}
                      {(open.seo_comments || []).map((c, i) => (
                        <div key={i} className="bg-lime-50 border border-lime-100 rounded-lg p-2">
                          <div className="text-xs font-bold text-lime-800">{c.name}</div>
                          <div className="text-sm text-slate-700">{c.text}</div>
                          <div className="text-[10px] text-slate-400 mt-1">{new Date(c.at).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                    {open.status === "posted" ? (
                      <div className="text-xs text-slate-400 italic">Blog is posted — comments are locked.</div>
                    ) : (
                      <>
                        <textarea rows={3} className="input-pill text-sm w-full mb-2" placeholder="Ask Head a question or flag an issue…" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="seo-comment-input" />
                        <button onClick={addComment} disabled={commentSubmit.submitting || !comment.trim()} className="btn-primary w-full py-2 text-sm disabled:opacity-50" data-testid="seo-comment-send">
                          {commentSubmit.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send to Head</>}
                        </button>
                      </>
                    )}
                  </div>

                  {open.status === "approved_to_seo" && (
                    <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4">
                      <div className="text-xs uppercase font-bold text-emerald-800 mb-2">Mark as Posted</div>
                      <input className="input-pill text-sm w-full mb-2" placeholder="Posted URL (optional)" value={postedUrl} onChange={(e) => setPostedUrl(e.target.value)} data-testid="seo-posted-url" />
                      <button onClick={markPosted} disabled={postSubmit.submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold w-full py-2 rounded-full text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="seo-mark-posted">
                        {postSubmit.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Marking…</> : <><CheckCircle2 className="w-4 h-4" /> Mark as Posted</>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}
